/**
 * Super-Admin → Registered Users drill-down modal.
 *
 * Opens when the "Total Registered Users" KPI card is clicked. Shows the
 * full user list with email / userCode / signup date / current status /
 * subscription / last login / docs generated, and a period filter
 * (Today · Week · Month · All).
 *
 * Design choices:
 *   • Uses the existing shadcn <Dialog />, <Table />, <Tabs />, <Badge />
 *     primitives — no new UI vocabulary introduced.
 *   • Server is the source of truth for filtering (so a tomorrow-cohort
 *     that gets imported later still appears correctly without client
 *     re-derivation).
 *   • A client-side search box layered on top — common SA workflow.
 *   • Renders 500 rows max per request; for >500 users we'll add pagination
 *     in a later iteration.
 */
import { useEffect, useMemo, useState } from 'react';
import { Search, Mail, Hash, CalendarDays, Circle, Crown, Clock, FileStack, RefreshCw } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../ui/dialog';
import { Tabs, TabsList, TabsTrigger } from '../ui/tabs';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from '../ui/table';
import api from '../../services/api';

const PERIODS = [
  { id: 'today', label: 'Today' },
  { id: 'week', label: 'This Week' },
  { id: 'month', label: 'This Month' },
  { id: 'all', label: 'All Time' },
];

const TIER_STYLES = {
  free:    'bg-slate-100 text-slate-700 border-slate-200',
  starter: 'bg-blue-100 text-blue-700 border-blue-200',
  pro:     'bg-violet-100 text-violet-700 border-violet-200',
  elite:   'bg-amber-100 text-amber-700 border-amber-200',
};

function fmtDate(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch { return '—'; }
}

export function RegisteredUsersDialog({ open, onOpenChange }) {
  const [period, setPeriod] = useState('all');
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');

  const load = async (p = period) => {
    setLoading(true); setError(null);
    try {
      const data = await api.getRegisteredUsers(p);
      setUsers(data?.users || []);
    } catch (e) {
      setError(e?.message || 'Failed to load users');
      setUsers([]);
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (open) load(period);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, period]);

  const filtered = useMemo(() => {
    if (!search.trim()) return users;
    const s = search.trim().toLowerCase();
    return users.filter((u) =>
      (u.email || '').toLowerCase().includes(s) ||
      (u.userCode || '').toLowerCase().includes(s) ||
      (u.name || '').toLowerCase().includes(s)
    );
  }, [users, search]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-6xl w-[96vw] max-h-[92vh] flex flex-col p-0 gap-0"
        data-testid="registered-users-dialog"
      >
        <DialogHeader className="px-5 sm:px-6 pt-5 pb-3 border-b border-slate-200 dark:border-slate-800">
          <DialogTitle className="flex items-center gap-2 text-lg">
            Registered Users
            <Badge variant="outline" className="text-[10px] font-medium">
              {filtered.length} of {users.length}
            </Badge>
          </DialogTitle>
          <DialogDescription className="text-xs text-muted-foreground">
            Real-time roster of every account ever created on the platform.
          </DialogDescription>
        </DialogHeader>

        {/* Filters bar */}
        <div className="px-5 sm:px-6 py-3 border-b border-slate-200 dark:border-slate-800 flex flex-col sm:flex-row sm:items-center gap-3">
          <Tabs value={period} onValueChange={setPeriod} className="w-full sm:w-auto" data-testid="period-filter">
            <TabsList className="grid grid-cols-4 w-full sm:w-auto">
              {PERIODS.map((p) => (
                <TabsTrigger key={p.id} value={p.id} data-testid={`period-${p.id}`} className="text-xs sm:text-sm">
                  {p.label}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
          <div className="relative flex-1 sm:max-w-xs">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search email, name, code…"
              className="pl-8"
              data-testid="users-search"
            />
          </div>
          <Button
            variant="outline"
            size="icon"
            onClick={() => load(period)}
            disabled={loading}
            aria-label="Refresh"
            data-testid="users-refresh"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>

        {/* Table */}
        <div className="flex-1 overflow-auto" data-testid="users-table-scroll">
          {error && (
            <div className="p-6 text-sm text-rose-600" data-testid="users-table-error">{error}</div>
          )}
          {!error && loading && filtered.length === 0 && (
            <div className="p-12 text-center text-sm text-muted-foreground" data-testid="users-table-loading">
              Loading…
            </div>
          )}
          {!error && !loading && filtered.length === 0 && (
            <div className="p-12 text-center text-sm text-muted-foreground" data-testid="users-table-empty">
              No users match the current filter.
            </div>
          )}
          {filtered.length > 0 && (
            <Table data-testid="users-table">
              <TableHeader className="sticky top-0 bg-white dark:bg-slate-950 z-10">
                <TableRow>
                  <TableHead className="min-w-[200px]"><div className="flex items-center gap-1.5"><Mail className="h-3.5 w-3.5" /> Email</div></TableHead>
                  <TableHead><div className="flex items-center gap-1.5"><Hash className="h-3.5 w-3.5" /> User Code</div></TableHead>
                  <TableHead><div className="flex items-center gap-1.5"><CalendarDays className="h-3.5 w-3.5" /> Signup Date</div></TableHead>
                  <TableHead><div className="flex items-center gap-1.5"><Circle className="h-3.5 w-3.5" /> Status</div></TableHead>
                  <TableHead><div className="flex items-center gap-1.5"><Crown className="h-3.5 w-3.5" /> Subscription</div></TableHead>
                  <TableHead><div className="flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" /> Last Login</div></TableHead>
                  <TableHead className="text-right"><div className="flex items-center gap-1.5 justify-end"><FileStack className="h-3.5 w-3.5" /> Documents</div></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((u, idx) => (
                  <TableRow key={u.userId || `${u.email}-${idx}`} data-testid={`user-row-${idx}`}>
                    <TableCell className="font-medium">
                      <div className="flex flex-col">
                        <span className="truncate max-w-[260px]">{u.email || '—'}</span>
                        {u.name && <span className="text-[11px] text-muted-foreground">{u.name}</span>}
                      </div>
                    </TableCell>
                    <TableCell><code className="text-xs bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 rounded">{u.userCode || '—'}</code></TableCell>
                    <TableCell className="text-sm">{fmtDate(u.signupAt)}</TableCell>
                    <TableCell>
                      {u.currentStatus === 'active' ? (
                        <span className="inline-flex items-center gap-1.5 text-xs font-medium text-emerald-700">
                          <span className="relative flex h-2 w-2">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                            <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
                          </span>
                          Active
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1.5 text-xs text-slate-500">
                          <span className="inline-flex rounded-full h-2 w-2 bg-slate-300" />
                          Inactive
                        </span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className={`text-[11px] capitalize ${TIER_STYLES[u.subscription] || TIER_STYLES.free}`}>
                        {u.subscription || 'free'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm">{fmtDate(u.lastLoginAt)}</TableCell>
                    <TableCell className="text-right tabular-nums font-medium">{(u.docsGenerated || 0).toLocaleString('en-IN')}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default RegisteredUsersDialog;
